import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Mail, ChevronDown, ChevronRight, Sparkles } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-CA", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

function shortRange(a, b) {
  const f = (iso) => new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "numeric" });
  return `${f(a)} → ${f(b)}`;
}

export default function AdminDigestLog() {
  const [entries, setEntries] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/digest-log").then((r) => { setEntries(r.data); setLoading(false); });
  }, []);

  return (
    <div data-testid="admin-digest-log">
      <div className="pf-chip pf-chip-lav">Audit log</div>
      <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">Weekly digest history</h2>
      <p className="text-sm text-slate-600 mt-1 max-w-2xl">
        Every digest delivered to master admins is recorded here — including summaries, the full data snapshot at send time, and every recipient email that was logged.
      </p>

      <div className="pf-card p-4 mt-6">
        {loading ? (
          <div className="py-8 text-center text-slate-500">Loading log…</div>
        ) : entries.length === 0 ? (
          <div className="py-12 text-center">
            <Mail className="w-8 h-8 mx-auto text-cyan-600 mb-3" />
            <div className="text-slate-900 font-medium">No digest has been sent yet.</div>
            <div className="text-sm text-slate-500 mt-1">The scheduler will fire Monday 09:00 UTC — or use "Send now" on the overview.</div>
          </div>
        ) : (
          <div className="divide-y divide-white/60">
            {entries.map((e) => {
              const open = openId === e.id;
              const d = e.data || {};
              return (
                <div key={e.id} className="py-3">
                  <button
                    onClick={() => setOpenId(open ? null : e.id)}
                    className="w-full flex items-center gap-3 rounded-xl hover:bg-white/60 p-2 -mx-2 transition-colors text-left"
                    data-testid={`digest-row-${e.id}`}
                  >
                    <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-medium text-slate-900">Weekly digest · {d.period_start && d.period_end ? shortRange(d.period_start, d.period_end) : "—"}</div>
                        <span className="pf-chip pf-chip-cyan">{d.new_merchants ?? 0} brands</span>
                        <span className="pf-chip pf-chip-lav">{d.new_coupons ?? 0} coupons</span>
                        <span className="pf-chip pf-chip-mint">{d.clicks_last_7 ?? 0} clicks</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">Sent {fmtDate(e.sent_at)}</div>
                    </div>
                    {open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                  </button>
                  {open && (
                    <div className="mt-2 ml-13 pl-13">
                      <p className="text-sm text-slate-700 leading-relaxed bg-white/60 rounded-xl p-4 border border-white/80">{e.summary}</p>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
                        <MiniStat label="New brands" value={d.new_merchants} />
                        <MiniStat label="New coupons" value={d.new_coupons} />
                        <MiniStat label="Clicks · 7d" value={d.clicks_last_7} />
                        <MiniStat label="Pending" value={(d.pending_stores || 0) + (d.pending_coupons || 0)} />
                      </div>

                      {d.new_stores && d.new_stores.length > 0 && (
                        <div className="mt-3 pf-card p-4">
                          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Brands that joined that week</div>
                          <div className="flex flex-wrap gap-2">
                            {d.new_stores.map((s, i) => (
                              <span key={i} className="pf-chip">{s.name} · {s.status}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {d.top_pending_coupons && d.top_pending_coupons.length > 0 && (
                        <div className="mt-3 pf-card p-4">
                          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Top pending coupons at send time</div>
                          <ul className="space-y-1 text-sm text-slate-700">
                            {d.top_pending_coupons.map((c, i) => (
                              <li key={i} className="flex justify-between gap-3">
                                <span className="truncate">{c.title}</span>
                                <span className="text-slate-500 whitespace-nowrap">{c.discount_value}{c.discount_type === "percent" ? "%" : ""}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-xl bg-white/60 border border-white/70 p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="font-display text-2xl text-slate-900 tabular-nums">{value ?? 0}</div>
    </div>
  );
}
